import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import {
  XMarkIcon,
  CreditCardIcon,
  TagIcon,
  GlobeAltIcon,
  UserGroupIcon,
  CalculatorIcon,
  QuestionMarkCircleIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  SparklesIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import Button from '@dashboard/components/Button';
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import checkIsConnectedToStripe from '@dashboard/services/seller/checkIsConnectedToStripe';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { tracking, useTracking } from '@dashboard/lib/tracking';
import useUserQueries from '@dashboard/hooks/queries/useUserQueries';
import usePageQueries from '@dashboard/hooks/queries/usePageQueries';
import ConnectStripeDialog from '@dashboard/components/ConnectStripeDialog';
import { differenceInDays } from 'date-fns';

const getSteps = (seller, { hasPages }) => [
  {
    title: 'Create your first page',
    description: 'Sell products, event tickets & subscriptions.',
    action: 'Create page',
    link: '/pages/new',
    icon: DocumentTextIcon,
    todo: !hasPages,
  },
  {
    title: 'Connect Stripe account',
    description: 'Start accepting payments securely.',
    action: 'Connect Stripe',
    link: '/settings/stripe',
    icon: CreditCardIcon,
    todo: !checkIsConnectedToStripe(seller),
  },
  {
    title: 'Add your branding',
    description: 'Upload your logo and customize colors.',
    action: 'Customize store',
    link: '/settings/general',
    icon: SparklesIcon,
    todo: !seller.logo,
  },
  {
    title: 'Add a custom domain',
    description: 'Personalize your store with your own domain.',
    action: 'Add custom domain',
    link: '/settings/domains',
    icon: GlobeAltIcon,
    todo: !seller.customDomain || !seller.customDomainVerified,
  },
  {
    title: 'Add email domain',
    description: 'Send confirmation emails from your own domain.',
    action: 'Add email domain',
    link: '/settings/email',
    icon: EnvelopeIcon,
    todo: !seller.customDomain || !seller.customDomainVerified,
  },
  {
    title: 'Invite team members',
    description: 'Collaborate with your team on your store.',
    action: 'Invite team members',
    link: '/settings/members',
    icon: UserGroupIcon,
    todo: !seller.teamMembers?.length,
  },
  {
    title: 'Create a coupon',
    description: 'Offer discounts to your customers.',
    action: 'Create coupon',
    link: '/coupons/new',
    icon: TagIcon,
    todo: true,
  },
  {
    title: 'Set up Stripe Tax',
    description: 'Automate tax calculations for your sales.',
    action: 'Set up Stripe Tax',
    link: '/settings/stripe-tax',
    icon: CalculatorIcon,
    todo: !seller.tax?.enabled || !seller.tax?.activatedInStripe,
  },
  {
    title: 'Visit help center',
    description: 'Your questions answered quickly.',
    action: 'Visit help center',
    link: '/help',
    target: '_blank',
    icon: QuestionMarkCircleIcon,
    todo: true,
  },
];

export default function OnboardingSteps() {
  useTracking();
  const { seller, user, setUser } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const { updateUser } = useUserQueries();
  const [hasPages, setHasPages] = useState(false);
  const { getPageCount } = usePageQueries();

  useEffect(() => {
    if (!seller._id) return;

    async function checkHasPages() {
      const { data } = await getPageCount(seller._id);
      setHasPages(data > 0);
    }

    checkHasPages();
  }, [seller._id]);

  useEffect(() => {
    if (!user?._id) return;

    // only show onboarding steps if the user is less than 14 days old
    if (differenceInDays(new Date(), new Date(user.createdAt)) < 14) {
      if (user.onboardingStepsClosed) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
    } else {
      setIsVisible(false);
    }
  }, [user]);

  const handleClose = () => {
    setIsVisible(false);
    localStorage.setItem('onboardingStepsClosed', 'true');
    tracking.trackEvent('Dashboard Get Started: Close');
    updateUser({ onboardingStepsClosed: true }, user._id);
    setUser({ ...user, onboardingStepsClosed: true });
  };

  if (!isVisible) return null;

  const steps = getSteps(seller, { hasPages });

  return (
    <Card className="mb-6 bg-gray-50 m-4 rounded-lg relative">
      <div className="absolute top-2 right-2">
        <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      <CardContent>
        <div className="mb-4">
          <h2 className="text-xl">Get started</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {steps.map((step, index) => (
            <div
              key={index}
              className={classNames('bg-white shadow-sm px-4 py-4 rounded-lg flex flex-col')}
            >
              <div className="flex items-center justify-between mb-3">
                <step.icon className="h-8 w-8 text-chocolate-1000" />

                {!step.todo && <CheckCircleIcon className="h-8 w-8 text-green-600" />}
              </div>

              <div className="flex items-center mb-1">
                <h3 className="text-lg font-semibold">{step.title}</h3>
              </div>

              <p className="text-gray-600 mb-2 text-sm leading-relaxed">{step.description}</p>

              {step.link === '/settings/stripe' && (
                <ConnectStripeDialog
                  trigger={
                    <Button appearance="secondary" className="self-start mt-auto" size="small">
                      {step.action} <ArrowRightIcon className="h-4 w-4 ml-2" />
                    </Button>
                  }
                />
              )}

              {step.link !== '/settings/stripe' && (
                <Button
                  href={step.link}
                  target={step.target}
                  onClick={() => tracking.trackEvent(`Dashboard Get Started: ${step.title}`)}
                  appearance="secondary"
                  className="self-start mt-auto"
                  size="small"
                >
                  {step.action} <ArrowRightIcon className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
